import React from 'react'
import './style/journey-wizard.scss'
import * as clone from 'clone'
import { TabbedView } from '../tabbed-view/tabbed-view'
import { getClassNames } from '../../_utils/classnames'
import { JourneyConfigurationTab } from './tabs/journey-configuration.tab'
import { JourneyBuilderTab } from './tabs/journey-builder.tab'
import { IJourneyWizard } from './interfaces'
import { serializeJourney } from '../../features/journeys/utils/journey-serialization'
import { AppState } from '../../stores/app'
import { JourneyService } from '../../services'
import { Container } from 'typescript-ioc/es5'
import { JourneyBuilderMode } from '../journey-builder/enums'
import { getJourneyStatusOnPublish, getPublishedJourneyStatusChange } from '../journey-builder/helpers/journey'
import { StatusType } from '../../enums/status-type'
import { simpleNotification } from '../../_utils/utils'
import { DeliveryChannel } from '@pushly/models/lib/enums/delivery-channels.enum'
import { Journey } from '../../features/journeys/types/journey-api-response'
import { StepType } from '@pushly/models/lib/enums/journeys'
import { isActionStep } from '../../features/journeys/types/journey-nodes'
import { IServiceApiResponse } from '../../interfaces/service-api-response'
import { ITabbedViewState } from '../tabbed-view/tabbed-view'

export class JourneyWizard extends TabbedView<IJourneyWizard, {}> {
    public state: ITabbedViewState = {
        tabs: [],
    }

    protected animated = false

    protected readonly appState: AppState
    protected readonly journeyService: JourneyService

    public constructor(props: IJourneyWizard) {
        super(props)

        this.appState = Container.get(AppState)
        this.journeyService = Container.get(JourneyService)
    }

    public componentDidMount() {
        this.loadTabsState().then()
    }

    public componentDidUpdate(prevProps: IJourneyWizard) {
        // ensure journey reload resets tabs
        if (!prevProps.journey && !!this.props.journey) {
            this.loadTabsState().then()
        }
    }

    public render() {
        return <div className={getClassNames('journey-wizard')}>{this.renderTabs()}</div>
    }

    protected async loadTabsState(): Promise<void> {
        const tabs = [
            {
                component: JourneyConfigurationTab,
                props: () => ({
                    ...this.props,
                    onChange: () => {},
                    onSubmit: this.handleConfigSubmit,
                    onSave: this.handleConfigSave,
                }),
            },
            {
                disabled: this.props.mode === JourneyBuilderMode.CREATE,
                component: JourneyBuilderTab,
                props: () => ({
                    ...this.props,
                    onSubmit: this.handleBuilderSubmit,
                }),
            },
        ]

        const activeTab = this.determineActiveTab(tabs)

        return this.setState({
            tabs,
            activeTab,
        })
    }

    /**
     * The configuration tab keeps the Active/Paused choice in its own state, and inactive tabs are
     * unmounted, so leaving the tab with that choice unsaved would silently discard it.
     */
    protected canLeaveActiveTab(): boolean {
        const activeTab = this.getCurrentTab()
        const configurationTab: JourneyConfigurationTab | null | undefined = this.tabRefs[JourneyConfigurationTab.tabName]
        const isLeavingConfigurationTab = !!activeTab && this.getTabName(activeTab) === JourneyConfigurationTab.tabName

        if (isLeavingConfigurationTab && configurationTab?.hasUnsavedStatusSwitchChange) {
            simpleNotification('error', 'You have an unsaved Active/Paused change. Please save before continuing.')
            return false
        }

        return true
    }

    protected handleConfigSubmit = async (journey: Journey) => {
        const res = await this.saveConfiguration(journey)
        if (res.ok) {
            if (this.props.mode === JourneyBuilderMode.CREATE) {
                this.appService.routeWithinDomain(`/journeys/${res.data!.id}/builder${location.search}`)
            } else {
                // The configuration was just saved, so there is nothing for the unsaved-changes guard to protect.
                await this.activateTab(JourneyBuilderTab.tabName)
            }
        }

        return res
    }

    protected handleConfigSave = async (journey: Journey) => {
        return this.saveConfiguration(journey)
    }

    protected handleBuilderSubmit = async (journey: Journey, remove?: boolean) => {
        const res = await this.saveSteps(journey, remove)

        if (res.ok) {
            // Reset builder dirty state before navigating so Prompt/beforeunload don't fire
            if (this.props.wizardRef && 'current' in this.props.wizardRef) {
                this.props.wizardRef.current?.markSaveComplete()
            }
            this.appService.routeWithinDomain('/journeys')
        }

        return res
    }

    protected saveConfiguration = async (journey: Journey) => {
        let res: IServiceApiResponse<Journey | undefined> = { ok: false, data: undefined }

        const opts = {
            showLoadingScreen: true,
            cancellationKey: 'cwiz.config.patch',
        }

        const data = serializeJourney(journey)

        if (this.props.mode === JourneyBuilderMode.CREATE) {
            // initial status via UI is SETUP
            data.status = StatusType.DRAFT.name
            data.type = data.campaign_type

            // persist initial trigger -> exit
            data.steps = journey.revision?.steps ?? []

            data.steps.forEach((step) => {
                if (step.type === StepType.ACTION) {
                    const stepConfig = clone(step.configuration ?? {})
                    const hasNotifBlock = !!stepConfig?.params?.notification

                    if (hasNotifBlock && stepConfig.params.notification?.channels) {
                        const channels = stepConfig.params.notification?.channels
                        const { default: defaults } = stepConfig.params.notification.template.channels
                        const newTemplate: any = {
                            default: defaults,
                        }

                        // re-assign new steps
                        for (const channel of channels) {
                            if (channel) {
                                const chKeyFormatted = channel.toLowerCase()
                                newTemplate[chKeyFormatted] =
                                    stepConfig.params.notification.template.channels[chKeyFormatted]

                                // strip actions when defaults should be applied for action enabled templates
                                if (channel === DeliveryChannel.WEB || channel === DeliveryChannel.NATIVE_ANDROID) {
                                    if (newTemplate[chKeyFormatted]?.display_meta?.override_default_actions === false) {
                                        if (newTemplate[chKeyFormatted].actions) {
                                            delete newTemplate[chKeyFormatted].actions
                                        }

                                        // UI has an all or nothing actions control - all actions are default is not overridden
                                        if (newTemplate.default.actions) {
                                            delete newTemplate.default.actions
                                        }
                                    }
                                }
                            }
                        }

                        stepConfig.params.notification.template.channels = newTemplate

                        step.configuration = stepConfig
                    }
                }
            })

            res = await this.journeyService.create(data, opts)
        } else if (data.id) {
            // pass null to remove end date value
            data.date_end = data.date_end ?? null

            // Status is sent only when the person changed it here; serializeJourney never carries it.
            const publishedStatusChange = getPublishedJourneyStatusChange(this.props.journey, journey)
            if (publishedStatusChange) {
                data.status = publishedStatusChange
            }

            // persist trigger changes
            data.steps = journey.revision?.steps ?? []

            // ensure un-edited action steps send notification_id only
            // this can only occur during config-edit
            data.steps.forEach((step) => {
                if (step.type === StepType.ACTION) {
                    const stepConfig = clone(step.configuration ?? {})
                    const hasNotifId = !!stepConfig?.params?.notification_id
                    const hasNotifBlock = !!stepConfig?.params?.notification

                    if (hasNotifId && hasNotifBlock) {
                        delete stepConfig.params.notification
                        step.configuration = stepConfig
                    }
                }
            })

            res = await this.journeyService.patch(data.id, data, opts)
        }

        if (res.ok) {
            if (this.props.mode === JourneyBuilderMode.CREATE) {
                simpleNotification(
                    'success',
                    <span>
                        Journey <strong>{data.name}</strong> successfully created.
                    </span>,
                )
            } else {
                simpleNotification(
                    'success',
                    <span>
                        Settings for journey <strong>{data.name}</strong> successfully updated.
                    </span>,
                )
            }

            if (res.data) {
                // Save & Continue switches to the builder tab right after this resolves, and the
                // builder tab must mount from the reloaded journey, not the one just saved over.
                const onSubmitResult: void | Promise<void> = this.props.onSubmit?.(res.data)
                await onSubmitResult
            }
        } else if (!res.cancelled) {
            simpleNotification('error', res.error?.message ?? 'Unable to save journey.')
        }

        return res
    }

    protected saveSteps = async (journey: Journey, remove?: boolean) => {
        const opts = {
            showLoadingScreen: true,
            cancellationKey: 'cwiz.builder.patch',
        }

        const exitStep = journey.steps.find((s) => s.type === StepType.EXIT)
        const globalTriggers = this.props.journey?.steps?.filter((s) => s.type === StepType.GLOBAL_TRIGGER) ?? []
        if (globalTriggers.length > 0) {
            // reset out_step_id to current exitStep
            globalTriggers.forEach((trigger) => {
                if (trigger.configuration.out_step_id !== exitStep?.id) {
                    trigger.configuration.out_step_id = exitStep?.id ?? null
                }

                const tIdx = journey.steps.findIndex((step) => step.id.toString() === trigger.id.toString())
                if (tIdx !== -1) {
                    journey.steps.splice(tIdx, 1, trigger)
                } else {
                    journey.steps.push(trigger)
                }
            })
        }

        // ensure un-edited action steps send notification_id only
        // parseJourneyFromFlow already projects notifications to IJourneyActionNotificationRequest shape
        journey.steps.forEach((step) => {
            if (!isActionStep(step)) return

            const hasNotifId = !!step.configuration.params.notification_id
            const hasNotifBlock = !!step.configuration.params.notification

            if (hasNotifId && hasNotifBlock) {
                delete step.configuration.params.notification
            }
        })

        const isPublishingDraft = journey.status === StatusType.DRAFT.name
        const statusOnPublish = getJourneyStatusOnPublish(journey)

        const data: Record<string, unknown> = {
            steps: journey.steps,
        }

        if (isPublishingDraft) {
            data.status = statusOnPublish
        }

        data.remove = !!remove

        const res = await this.journeyService.patch(journey.id, data, opts)

        if (res.ok) {
            if (isPublishingDraft) {
                simpleNotification(
                    'success',
                    statusOnPublish === StatusType.PAUSED.name ? (
                        <span>
                            Journey <strong>{journey.name}</strong> published and paused. Nobody enters it until it is
                            resumed.
                        </span>
                    ) : (
                        <span>
                            Journey <strong>{journey.name}</strong> published and active.
                        </span>
                    ),
                )
            } else {
                simpleNotification(
                    'success',
                    <span>
                        Journey <strong>{journey.name}</strong> successfully updated.
                    </span>,
                )
            }

            if (res.data) {
                this.props.onSubmit?.(res.data)
            }
        } else if (!res.cancelled) {
            simpleNotification('error', res.error?.message ?? 'Unable to save journey.')
        }

        return res
    }
}
